import { groupBy } from 'lodash-es';
import {
  AssignedUsersByGroup,
  CaseClinicalSpecialistResource,
  CaseResource,
  CaseResourceCombined,
  CaseResourceType,
  CaseRetrievalSurgeonResource,
  EmployeeLabels,
} from '@/api/apis/case';
import { caseManagementGroups } from '@/constants/user.constants';
import { getFullName, sortUsersByGroup } from '@/utils/helpers';
import { roleByCaseUserGroup } from '@/constants/case/case.constants';
import { EmployeeRole } from '@/api/apis/resources';
import { GroupWeightType } from '@/types';
import { sortClinicalSpecialistsByEmployeeLabel } from '@/utils/helpers/resources.helpers';

export const mapResources = (
  resources?: (CaseResource<CaseClinicalSpecialistResource> | CaseResource<CaseRetrievalSurgeonResource>)[],
) =>
  resources?.map(({ resourceData, resourceId }) => {
    const { id, firstName, lastName, profilePhoto, resourceType, roles, labels } = resourceData ?? {};

    let roleOnCase =
      resourceType === CaseResourceType.ClinicalSpecialist
        ? EmployeeRole.ClinicalSpecialist
        : EmployeeRole.RetrievalSurgeon;

    if (resourceType === CaseResourceType.Surgeon && roles?.includes(EmployeeRole.ContractorRetrievalSurgeon)) {
      roleOnCase = EmployeeRole.ContractorRetrievalSurgeon;
    }

    return {
      id: id ?? resourceId,
      employeeId: resourceId,
      name: getFullName(firstName, lastName),
      roleOnCase,
      photo: profilePhoto,
      labels,
    };
  }) ?? [];

export const getUsers = (users: AssignedUsersByGroup) => {
  const assignedEmployees = caseManagementGroups.flatMap((group) => users[group] || []);

  return assignedEmployees.map((u) => ({
    id: u.userId ?? u?.employeeId,
    employeeId: u.employeeId,
    name: getFullName(u.user.firstName, u.user.lastName),
    roleOnCase: roleByCaseUserGroup[u.group],
    photo: u.user.photoPath,
  }));
};

export const getFullTeam = (resources: CaseResourceCombined[], assignedEmployees: AssignedUsersByGroup) => {
  const resourceGroups = groupBy(resources, 'resourceType');
  const clinicalSpecialists = (resourceGroups[CaseResourceType.ClinicalSpecialist] ??
    []) as CaseResource<CaseClinicalSpecialistResource>[];
  const surgeons = (resourceGroups[CaseResourceType.Surgeon] ?? []) as CaseResource<CaseRetrievalSurgeonResource>[];
  const usersMapped = getUsers(assignedEmployees);
  const logisticsResources = [...clinicalSpecialists, ...surgeons];
  const logisticsResourcesMapped = mapResources(logisticsResources);

  return [...logisticsResourcesMapped, ...usersMapped];
};

export const getResourcesTeam = (resources: CaseResourceCombined[]) => {
  const resourceGroups = groupBy(resources, 'resourceType');
  const clinicalSpecialists = (resourceGroups[CaseResourceType.ClinicalSpecialist] ??
    []) as CaseResource<CaseClinicalSpecialistResource>[];
  const sortedCsList = sortClinicalSpecialistsByEmployeeLabel(clinicalSpecialists);
  const surgeons = (resourceGroups[CaseResourceType.Surgeon] ?? []) as CaseResource<CaseRetrievalSurgeonResource>[];
  const employeeSurgeons = surgeons.filter((s) => s.resourceData?.roles?.includes(EmployeeRole.RetrievalSurgeon));
  const contractorSurgeons = surgeons.filter((s) =>
    s.resourceData?.roles?.includes(EmployeeRole.ContractorRetrievalSurgeon),
  );
  const logisticsResources = [...sortedCsList, ...employeeSurgeons, ...contractorSurgeons];

  return mapResources(logisticsResources);
};

export const getManagementTeam = (assignedEmployees: AssignedUsersByGroup, groupWeight: GroupWeightType) => {
  const list = caseManagementGroups.flatMap((group) => assignedEmployees[group] || []);

  return sortUsersByGroup(list, groupWeight, true).map((u) => ({
    id: u.userId ?? u?.employeeId,
    employeeId: u.employeeId,
    name: getFullName(u.user.firstName, u.user.lastName),
    roleOnCase: roleByCaseUserGroup[u.group],
    photo: u.user.photoPath,
    labels: u.isPrimary ? [EmployeeLabels.Primary] : [],
  }));
};
